import { useEffect, useState } from "react";
import axios from "../api/client";
import { useTranslation } from "react-i18next";

function formatBytes(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const idx = Math.floor(Math.log(value) / Math.log(1024));
  return `${(value / 1024 ** idx).toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
}

export default function AdminDbStats() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadStats() {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get("/api/admin/db-stats");
      setStats(data);
    } catch (err) {
      console.error("Failed to load DB stats", err);
      setError(err.response?.data?.message || t("admin.database.errors.stats"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadStats().catch(() => {});
  }, [t]);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="bg-white border rounded-xl shadow p-4 flex flex-wrap gap-4 justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold mb-1">{t("admin.database.title")}</h1>
          <p className="text-gray-600">{t("admin.database.description")}</p>
        </div>
        <button onClick={loadStats} disabled={loading} type="button" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60">
          {loading ? t("admin.database.refreshing") : t("admin.database.refreshDatabase")}
        </button>
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
      {stats && (
        <div className="grid gap-4">
          <div className="bg-white border rounded-xl shadow p-4 flex flex-wrap gap-4">
            <div><p className="text-sm text-gray-500 uppercase tracking-wide">{t("admin.database.totalUsage")}</p><p className="text-2xl font-semibold">{formatBytes(stats.totalBytes)}</p></div>
            <div><p className="text-sm text-gray-500 uppercase tracking-wide">{t("admin.database.databases")}</p><p className="text-2xl font-semibold">{stats.databases?.length || 0}</p></div>
            {stats.collectedAt && <div><p className="text-sm text-gray-500 uppercase tracking-wide">{t("admin.database.collected")}</p><p className="text-lg">{new Date(stats.collectedAt).toLocaleString("fi-FI")}</p></div>}
          </div>
          {stats.databases?.map((db) => (
            <div key={db.name} className="bg-white border rounded-xl shadow">
              <div className="border-b px-4 py-3"><p className="text-lg font-semibold">{db.name}</p><p className="text-sm text-gray-500">{t("admin.database.databaseSummary", { size: formatBytes(db.sizeBytes), count: db.tables.length })}</p></div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm"><thead><tr className="border-b text-gray-600"><th className="py-2 px-3 text-left">{t("admin.database.table")}</th><th className="py-2 px-3 text-right">{t("admin.database.rows")}</th><th className="py-2 px-3 text-right">{t("admin.database.data")}</th><th className="py-2 px-3 text-right">{t("admin.database.index")}</th><th className="py-2 px-3 text-right">{t("admin.database.total")}</th></tr></thead>
                  <tbody>{db.tables.map((table) => <tr key={table.name} className="border-b last:border-b-0"><td className="py-2 px-3">{table.name}</td><td className="py-2 px-3 text-right">{table.rows?.toLocaleString?.("fi-FI") ?? "-"}</td><td className="py-2 px-3 text-right">{formatBytes(table.dataBytes)}</td><td className="py-2 px-3 text-right">{formatBytes(table.indexBytes)}</td><td className="py-2 px-3 text-right"><span className="font-medium">{formatBytes(table.totalBytes)}</span></td></tr>)}</tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
